import axios from 'axios'
import type { AxiosInstance, AxiosRequestConfig } from 'axios'
import sharedConfig from '../config'
import { setRequestSign } from '../helper/requestSign'
import { ResultEnum } from '../enums'
import { HttpServerError } from './error'
import type { SignSecret } from '../types'
import { startTest, getLine, clearLine } from './wayfinder'
import { getSessionId } from '../helper/session'
import { getServiceLineConfig } from '../config/service'
import type { ServiceType } from '../config/service'

// 定义一个新的实例类型，它的请求方法直接返回响应数据而不是整个响应对象
export interface CustomAxiosInstance extends Omit<AxiosInstance, 'get' | 'post' | 'put' | 'delete'> {
  request<T = any, R = T>(config: AxiosRequestConfig): Promise<R>
  get<T = any, R = T>(url: string, config?: AxiosRequestConfig): Promise<R>
  post<T = any, R = T>(url: string, data?: any, config?: AxiosRequestConfig): Promise<R>
  put<T = any, R = T>(url: string, data?: any, config?: AxiosRequestConfig): Promise<R>
  delete<T = any, R = T>(url: string, config?: AxiosRequestConfig): Promise<R>
}

/** 创建请求实例 */
export function createService(
  options: {
    // 服务类型
    serviceType: ServiceType
    // 基础路径
    basePath: string

    // 添加公共参数
    addCommon?: boolean
    // 获取签名密钥
    getSecret?: () => SignSecret
  } = { serviceType: 'seaart', basePath: '' },
  axiosConfig: any = {},
) {
  const { gateway, backupLines } = getServiceLineConfig(options.serviceType)

  const defaultConfig = {
    baseURL: gateway,
    headers: { 'Content-Type': 'application/json' },
    timeout: 30000,
  }

  // 合并基础路径
  if (options.basePath) {
    defaultConfig.baseURL = defaultConfig.baseURL + options.basePath
  }

  Object.assign(defaultConfig, axiosConfig)

  // 创建一个 axios 实例命名为 service
  const service = axios.create(defaultConfig) as unknown as CustomAxiosInstance

  // ─── 线路择优 ──────────────────────────────────────────
  // optimalLinePromise 存在时，请求拦截器会 await 它以获取最优线路。
  // 同时充当防抖标志：非 undefined 表示正在测速中，避免并发网络错误重复触发 clearLine。
  let optimalLinePromise: Promise<string | void> | undefined

  /** 等待测速结果并将最优线路写入 service.defaults.baseURL */
  function applyOptimalLine() {
    optimalLinePromise = getLine(options.serviceType).then((line) => {
      optimalLinePromise = undefined
      const baseURL = line + options.basePath
      service.defaults.baseURL = baseURL
      return baseURL
    })
  }

  // 存在备用线路时立即触发测速
  if (backupLines?.length) {
    startTest(options.serviceType)
    applyOptimalLine()
  }

  // ─── 请求拦截 ─────────────────────────────────────────
  service.interceptors.request.use(
    async (config) => {
      // 测速未完成时阻塞请求，等待最优线路确定后再发出
      if (optimalLinePromise) {
        const baseURL = (await optimalLinePromise) as string
        config.baseURL = baseURL
      }

      // 添加公共参数
      if (options.addCommon && config.data) {
        const st_common = config.data['st_common'] || {}
        if (sharedConfig.clientId) {
          st_common['client_id'] = sharedConfig.clientId
        }
        config.data['st_common'] = st_common
      }

      // 注入全局 session_id（仅在已初始化时注入，签名前写入以参与签名）
      const sessionId = getSessionId()
      if (sessionId && config.data) {
        // 注入 session_id
        const st_common = config.data['st_common'] || {}
        st_common['st_session_id'] = sessionId
        config.data['st_common'] = st_common
      }

      const secret = options.getSecret?.() || sharedConfig.secret
      if (secret) await setRequestSign(config, secret)

      return config
    },
    // 发送失败
    (error) => Promise.reject(error),
  )
  // ─── 响应拦截 ─────────────────────────────────────────
  service.interceptors.response.use(
    (response) => {
      const { data } = response

      // 业务码非成功时抛出 HttpServerError，由上层按需处理
      if (data.code !== ResultEnum.SUCCESS) {
        return Promise.reject(new HttpServerError(data.message, data.code))
      }

      return data.data
    },
    (error) => {
      // 网络层错误（无 response）且非主动取消 → 线路可能故障，清除线路并重新测速
      // optimalLinePromise 为空才触发，避免并发请求失败时重复 clearLine
      if (!error.response && error.code !== 'ERR_CANCELED' && backupLines?.length) {
        if (!optimalLinePromise) {
          clearLine(options.serviceType)
          applyOptimalLine()
        }
      }
      return Promise.reject(error)
    },
  )

  return service
}

// 创建海艺请求
export const createSeaartService = (options: { basePath: string; addCommon?: boolean } = { basePath: '' }) => {
  return createService({
    serviceType: 'seaart',
    ...options,
  })
}

// 创建发行请求
export const createPublishService = (options: { basePath: string; addCommon?: boolean } = { basePath: '' }) => {
  return createService({
    serviceType: 'publish',
    ...options,
  })
}
